import _ from "lodash";
import { assert } from "../utils/error";
import { ChordStructure } from "./chords";
import {
    ChordTones,
    HarmonicFieldPatterns,
    Note,
    Tone,
    ToneIndex
} from "./core";


export class IntervalPattern {
    name: string;
    intervals: number[];

    constructor(name: string, intervals: number[]) {
        // assert(name, 'IntervalPattern name is required');
        // assert(intervals, 'IntervalPattern intervals is required');
        // assert(typeof intervals === typeof [0], 'IntervalPattern intervals must be an array');
        assert(intervals.length > 0, 'IntervalPattern intervals must have at least one element');
        this.name = name;
        this.intervals = intervals;
    }

    toActiveSteps(): number[] {
        const activeSteps: number[] = [0];
        for (let i = 0; i < this.intervals.length; i++) {
            const interval = this.intervals[i];
            if (interval > 0) {
                activeSteps.push(interval + activeSteps[i]);
            }
        }
        return activeSteps;
    }
}
function rotateArray<T>(arr: T[], steps: number): T[] {
    const len = arr.length;
    const s = ((steps % len) + len) % len; // handle negative steps
    return arr.slice(s).concat(arr.slice(0, s));
}


export class Tonality {
    center: Tone;
    scalePattern: ScalePattern;
    modeShift: number;
    toneIndexes: ToneIndex[];

    constructor(
        center: Tone,
        scalePattern: ScalePattern,
        modeShift: number = 0
    ) {
        // assert(center, 'Tonality center is required');
        // assert(center instanceof Tone, `Tonality center must be a Tone. was ${center}`);
        // assert(intervalPattern, 'Tonality intervalPattern is required');
        // assert(intervalPattern instanceof IntervalPattern, `Tonality intervalPattern must be an IntervalPattern. was ${intervalPattern}`);
        this.center = center;
        this.modeShift = modeShift;
        this.scalePattern = scalePattern;
        let last = center.index;
        this.toneIndexes = [];


        rotateArray<number>(this.scalePattern.pattern.intervals, this.modeShift).forEach((interval) => {
            last += interval;
            this.toneIndexes.push(last % 12 as ToneIndex);
        });
        this.toneIndexes = this.toneIndexes.sort((a, b) => a - b);
    }

    /**
     * @deprecated This method is deprecated. Use Tonality.containsTone() instead.
     */
    containsNote(note: Note | Tone): boolean {
        return Tonality.containsTone(this, note instanceof Note ? note.getTone() : note);
    }

    static containsTone(
        tonality: Tonality,
        tone: Note | Tone
    ): boolean {
        if (!tonality || !tonality.toneIndexes) {
            return false;
        }
        const r = tone.index % 12 as ToneIndex;
        return tonality.toneIndexes.includes(r);
    }

    getTones(): Tone[] {
        return this.toneIndexes.map(Tone.fromIndex);
    }

    // FIXME: taka this method out of the model. It doesn't belong here.
    getHarmonicFieldChords(): ChordTones[][] {

        return this.scalePattern
            .buildHarmonicFieldPatterns()
            .map((chord: ChordStructure[]) => {
                return chord.map((cs: ChordStructure) => {
                    return cs.map((i: number) => this.center.up(i));
                });
            });
    }
    toString(): string {
        return `${this.center.getName()} ${this.scalePattern.name} ${this.modeShift > 0 ? `(mode ${this.modeShift})` : ''}`;
    }
}

export type ScalePatternId = string;

export class ScalePattern {
    public readonly id: ScalePatternId;
    name?: string;

    public readonly steps: number[];
    public readonly pattern: IntervalPattern;

    constructor(pattern: IntervalPattern, id?:  ScalePatternId, name?: string) {
        if (!pattern || !pattern.intervals || pattern.intervals.length === 0) {
            throw new Error("ScalePattern requires a valid IntervalPattern with non-empty intervals");
        }
        this.pattern = pattern;
        this.id = id || this.generateIdFromPattern(pattern);

        // Calculate the steps by accumulating the intervals.
        this.steps = [];
        let cumulative = 0;
        this.steps.push(cumulative); // start with the root (step 0)

        for (const interval of this.pattern.intervals) {
            cumulative += interval;
            this.steps.push(cumulative);
        }

        // If a name is provided, use it; otherwise, build it from the note names of the steps.
        if (name) {
            this.name = name;
        } else {
            this.name = this.steps
                .map(step => Tone.fromIndex(step % 12 as ToneIndex).getName())
                .join(" ");
        }
    }

    private generateIdFromPattern(pattern: IntervalPattern): ScalePatternId {
        return pattern.intervals.join("-");
    }

    buildHarmonicFieldPatterns(): HarmonicFieldPatterns {
        return _.range(3, 8).map((nadic) => {
            return _.range(0, 7).map((root) => {
                return _.range(0, nadic).map((i) => i * 2 + root).map((i) => {
                    return this.steps[i % this.steps.length] as number;
                });
            })
        });
    }

    getIntervals(): number[] {
        return [...this.pattern.intervals];
    }
}

export type ScaleDegree = number;

export class Scale {
    name: string;
    root: Tone;
    pattern: ScalePattern;
    noteIndexes: number[];
    tones: Tone[];
    // Ordered scale degrees as pitch classes (0 = root, 1 = second, etc)
    private degrees: number[];

    constructor(root: Tone, pattern: ScalePattern) {
        if (!pattern) {
            throw new Error("Scale pattern is required");
        }
        this.pattern = pattern;
        this.root = root;

        // Construct the scale name.
        if (this.pattern.name) {
            this.name = `${this.root.getName()} ${this.pattern.name}`;
        } else {
            let current = this.root.index;
            const names = [this.root.getName()];
            // Build the name by adding successive note names from intervals.
            this.pattern.getIntervals().forEach(interval => {
                current += interval;
                names.push(Tone.fromIndex(current % 12 as ToneIndex).getName());
            });
            this.name = names.join(" ");
        }

        // Build the ordered scale degrees (pitch classes) from the root using pattern intervals.
        let currentIndex = this.root.index;
        this.degrees = [currentIndex % 12];
        this.pattern.getIntervals().forEach(interval => {
            currentIndex += interval;
            this.degrees.push(currentIndex % 12);
        });

        this.tones = this.degrees.map(d => Tone.fromIndex(d as ToneIndex));

        // Build the scale note indexes (unique pitch classes, sorted for convenience).
        this.noteIndexes = _.sortBy(Array.from(new Set(this.degrees)));
    }

    // Checks if the scale contains a note represented by a Tone or a Note.
    contains(noteOrTone: Tone | Note): boolean {
        const noteIndex = noteOrTone.index % 12;
        return this.noteIndexes.includes(noteIndex);
    }

    // Returns the Tone corresponding to a given scale degree (0-based index; 0 is the root).
    getToneForDegree(degree: ScaleDegree): Tone {
        if (degree < 0 || degree >= this.degrees.length) {
            throw new Error(`Invalid scale degree. Must be between 0 and ${this.degrees.length - 1}`);
        }
        return Tone.fromIndex(this.degrees[degree] as ToneIndex);
    }

    // Returns the scale degree (0-based index) of a given Tone or Note.
    getScaleDegreeOfTone(noteOrTone: Tone | Note): ScaleDegree {
        const noteIndex = noteOrTone.index % 12;
        const degree = this.degrees.findIndex(d => d % 12 === noteIndex);
        if (degree === -1) {
            throw new Error(`The note ${noteOrTone.getName()} is not in this scale`);
        }
        return degree;
    }
}
